// components/MockInterview/MockInterviewSummary.js
import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Calendar, Clock, Brain, Target, CheckCircle,
  AlertTriangle, AlertCircle, ChevronDown, ChevronUp,
  MessageSquare, BarChart3, TrendingUp, Star, Play,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppButton, AppBadge, AppCard, AppCardContent,
  AppCardHeader, AppCardTitle, Separator,
} from "../ui";
import "./mock-interview.scss";

function getScoreColor(score) {
  if (score >= 80) return "mis-text--emerald";
  if (score >= 60) return "mis-text--amber";
  return "mis-text--red";
}

function getScoreBgClass(score) {
  if (score >= 80) return "mis-score-bg--high";
  if (score >= 60) return "mis-score-bg--mid";
  return "mis-score-bg--low";
}

function getScoreBarColor(score) {
  if (score >= 80) return "emerald";
  if (score >= 60) return "amber";
  return "red";
}

function getGrade(score) {
  if (score >= 90) return { grade: "A+", label: "Exceptional" };
  if (score >= 80) return { grade: "A", label: "Excellent" };
  if (score >= 70) return { grade: "B+", label: "Good" };
  if (score >= 60) return { grade: "B", label: "Above Average" };
  if (score >= 50) return { grade: "C", label: "Average" };
  return { grade: "D", label: "Needs Improvement" };
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    weekday: "long", month: "long", day: "numeric", year: "numeric",
  });
}

function scoreClass(s) {
  if (s >= 80) return "mis-score--high";
  if (s >= 50) return "mis-score--mid";
  return "mis-score--low";
}

const MockInterviewSummary = () => {
  const { mockInterviewId } = useParams();
  const [loading, setLoading] = useState(true);
  const [interview, setInterview] = useState(null);
  const [error, setError] = useState("");
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchSummary = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.get(`/mock-interview/${mockInterviewId}`);
        setInterview(data);
      } catch {
        setError("Failed to load summary.");
      }
      setLoading(false);
    };
    fetchSummary();
  }, [mockInterviewId]);

  if (loading) return (
    <div className="mis-loading-page">
      <Spinner animation="border" size="sm" />
      <span>Loading summary...</span>
    </div>
  );

  if (error) return (
    <div className="mis-error">
      <AlertCircle size={20} />
      <span>{error}</span>
    </div>
  );

  if (!interview) return null;

  const mockData = interview.mock || interview;
  const { summary, overallScore, config, questions, createdAt, student } = mockData;
  const score = Math.round(overallScore || 0);
  const { grade, label } = getGrade(score);
  const answered = questions.filter((q) => q.answer).length;
  const tech = config?.technology || config?.topics?.[0] || "—";
  const type = config?.interviewType || "Technical";
  const diff = config?.difficulty || "—";
  const dur = config?.duration || "—";
  const roleName = config?.role || "—";

  // Determine if summary is structured JSON or plain text
  const isStructuredSummary = summary && typeof summary === "object" && summary.overallGrade;

  // Use structured summary data if available, else compute from questions
  let categoryAverages;
  let strengths = [];
  let improvements = [];
  let summaryText = "";
  let interviewReadiness = "";
  let detailedFeedback = "";
  let overallGrade = "";

  if (isStructuredSummary) {
    overallGrade = summary.overallGrade || "";
    summaryText = summary.overallSummary || "";
    interviewReadiness = summary.interviewReadiness || "";
    detailedFeedback = summary.detailedFeedback || "";
    strengths = summary.strengths || [];
    improvements = [...(summary.weaknesses || []), ...(summary.topicsToImprove || [])];

    // Use categoryScores from AI
    const catScores = summary.categoryScores || {};
    categoryAverages = Object.entries(catScores).map(([key, val]) => ({
      category: key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim(),
      score: val?.score || 0,
      comment: val?.comment || "",
    })).sort((a, b) => b.score - a.score);
  } else {
    // Legacy: compute category averages from question scores
    const categoryScoresCalc = questions.reduce((acc, q) => {
      const cat = q.aiMeta?.stage || "General";
      if (!acc[cat]) acc[cat] = { total: 0, count: 0 };
      if (q.score != null) { acc[cat].total += q.score; acc[cat].count += 1; }
      return acc;
    }, {});
    categoryAverages = Object.entries(categoryScoresCalc)
      .map(([cat, d]) => ({ category: cat.charAt(0).toUpperCase() + cat.slice(1), score: d.count ? Math.round(d.total / d.count) : 0 }))
      .sort((a, b) => b.score - a.score);

    // Extract strengths / improvements from summary text (basic)
    summaryText = typeof summary === "string" ? summary : "";
    if (summaryText) {
      const lines = summaryText.split(/[\n•-]/).map((l) => l.trim()).filter(Boolean);
      let section = "";
      lines.forEach((line) => {
        if (/strength|well|good|excell/i.test(line)) section = "strength";
        if (/improv|weak|better|work on/i.test(line)) section = "improve";
        if (section === "strength" && line.length > 10) strengths.push(line);
        else if (section === "improve" && line.length > 10) improvements.push(line);
      });
    }
  }

  return (
    <div className="mi-page">
      {/* Header with back button */}
      <div className="mis-result-header">
        <button type="button" className="mis-result-header__back" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} />
        </button>
        <div>
          <div className="mis-result-header__title-row">
            <h1 className="mis-result-header__title">{tech} {type} Interview</h1>
            <AppBadge variant="secondary">{diff}</AppBadge>
          </div>
          <p className="mis-result-header__date">
            <Calendar size={12} /> {formatDate(createdAt)}
            {student?.name && <> &middot; <strong>{student.name}</strong></>}
          </p>
        </div>
      </div>

      <div className="mis-result-layout">
        {/* Left column */}
        <div className="mis-result-left">
          {/* Overall Score Card */}
          <AppCard>
            <AppCardContent>
              <div className="mis-result-score-card">
                <div className={`mis-result-score-circle ${getScoreBgClass(score)}`}>
                  <div className="mis-result-score-circle__inner">
                    <p className={`mis-result-score-circle__value ${getScoreColor(score)}`}>{score}</p>
                    <p className="mis-result-score-circle__sub">out of 100</p>
                  </div>
                  <div className={`mis-result-score-circle__grade ${getScoreBgClass(score)}`}>
                    <span className={getScoreColor(score)}>{grade}</span>
                  </div>
                </div>
                <div className="mis-result-score-info">
                  <p className={`mis-result-score-info__label ${getScoreColor(score)}`}>{label}</p>
                  <p className="mis-result-score-info__sub">
                    You answered {answered} of {questions.length} questions
                  </p>
                  <div className="mis-result-score-info__meta">
                    <span><Clock size={14} /> {dur} min session</span>
                    <span><Brain size={14} /> {questions.length} questions</span>
                    <span><Target size={14} /> {roleName}</span>
                  </div>
                </div>
              </div>
            </AppCardContent>
          </AppCard>

          {/* Category-wise Performance */}
          {categoryAverages.length > 0 && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle className="mis-result-section-title">
                  <BarChart3 size={16} /> Category-wise Performance
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <div className="mis-result-categories">
                  {categoryAverages.map((cat) => (
                    <div key={cat.category} className="mis-result-category">
                      <div className="mis-result-category__header">
                        <span className="mis-result-category__name">{cat.category}</span>
                        <span className={`mis-result-category__score ${getScoreColor(cat.score)}`}>{cat.score}%</span>
                      </div>
                      <div className="mis-result-category__bar">
                        <div
                          className={`mis-result-category__fill mis-result-category__fill--${getScoreBarColor(cat.score)}`}
                          style={{ width: `${cat.score}%` }}
                        />
                      </div>
                      {cat.comment && (
                        <p className="mis-result-category__comment">{cat.comment}</p>
                      )}
                    </div>
                  ))}
                </div>
              </AppCardContent>
            </AppCard>
          )}

          {/* AI Summary */}
          {summaryText && (
            <AppCard className="mis-summary-card">
              <AppCardContent>
                <h5 className="mis-summary-card__title">
                  <CheckCircle size={16} /> AI Summary
                  {overallGrade && (
                    <AppBadge variant="secondary" className="mis-summary-card__grade">
                      Grade: {overallGrade}
                    </AppBadge>
                  )}
                </h5>
                <p className="mis-summary-card__text">{summaryText}</p>
                {interviewReadiness && (
                  <div className="mis-summary-card__readiness">
                    <Target size={14} />
                    <span>Interview Readiness: <strong>{interviewReadiness}</strong></span>
                  </div>
                )}
                {detailedFeedback && (
                  <div className="mis-summary-card__detailed">
                    <p className="mis-summary-card__detailed-label">Detailed Feedback</p>
                    <p className="mis-summary-card__detailed-text">{detailedFeedback}</p>
                  </div>
                )}
              </AppCardContent>
            </AppCard>
          )}

          {/* Question-wise Analysis */}
          <AppCard>
            <AppCardHeader>
              <AppCardTitle className="mis-result-section-title">
                <MessageSquare size={16} /> Question-wise Analysis
              </AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="mis-result-questions">
                {questions.map((q, idx) => {
                  const isExpanded = expandedQuestion === idx;
                  const qScore = q.score == null ? null : Math.round(q.score);
                  return (
                    <div key={q.aiQuestion || `q-${idx}`} className="mis-result-question">
                      <button
                        type="button"
                        className="mis-result-question__header"
                        onClick={() => setExpandedQuestion(isExpanded ? null : idx)}
                      >
                        <div className={`mis-result-question__score-box ${qScore == null ? "" : getScoreBgClass(qScore)}`}>
                          {qScore == null ? "—" : qScore}
                        </div>
                        <div className="mis-result-question__title">
                          <p className="mis-result-question__text">Q{idx + 1}: {q.aiQuestion}</p>
                          <div className="mis-result-question__badges">
                            {q.aiMeta?.stage && <AppBadge variant="outline" size="sm">{q.aiMeta.stage}</AppBadge>}
                          </div>
                        </div>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      {isExpanded && (
                        <div className="mis-result-question__body">
                          <Separator />
                          <div className="mis-result-question__section">
                            <p className="mis-result-question__section-label">Question</p>
                            <p className="mis-result-question__section-text">{q.aiQuestion}</p>
                          </div>
                          <div className="mis-result-question__section">
                            <p className="mis-result-question__section-label">Your Answer</p>
                            <div className="mis-result-question__answer-box">
                              {q.answer || <span className="mis-qa-card__empty">No answer provided</span>}
                            </div>
                          </div>
                          {q.code && (
                            <div className="mis-result-question__section">
                              <p className="mis-result-question__section-label">Code</p>
                              <pre className="mis-qa-card__code">{q.code}</pre>
                            </div>
                          )}
                          {q.feedback && (
                            <div className="mis-result-question__section">
                              <p className="mis-result-question__section-label">AI Feedback</p>
                              <div className="mis-result-question__feedback-box">{q.feedback}</div>
                            </div>
                          )}
                          <div className="mis-result-question__footer">
                            <span className={qScore == null ? "" : getScoreColor(qScore)}>
                              Score: {qScore == null ? "—" : `${qScore}/100`}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </AppCardContent>
          </AppCard>
        </div>

        {/* Right sidebar */}
        <div className="mis-result-right">
          {/* Strengths */}
          {strengths.length > 0 && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle className="mis-result-section-title">
                  <CheckCircle size={16} className="mis-icon--emerald" /> Strengths
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <ul className="mis-result-list">
                  {strengths.slice(0, 5).map((s) => (
                    <li key={s}><Star size={14} className="mis-icon--emerald" /> <span>{s}</span></li>
                  ))}
                </ul>
              </AppCardContent>
            </AppCard>
          )}

          {/* Improvements */}
          {improvements.length > 0 && (
            <AppCard>
              <AppCardHeader>
                <AppCardTitle className="mis-result-section-title">
                  <TrendingUp size={16} className="mis-icon--amber" /> Areas for Improvement
                </AppCardTitle>
              </AppCardHeader>
              <AppCardContent>
                <ul className="mis-result-list">
                  {improvements.slice(0, 5).map((imp) => (
                    <li key={imp}><AlertTriangle size={14} className="mis-icon--amber" /> <span>{imp}</span></li>
                  ))}
                </ul>
              </AppCardContent>
            </AppCard>
          )}

          {/* Session Details */}
          <AppCard>
            <AppCardHeader>
              <AppCardTitle>Session Details</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="mis-summary-rows">
                <div className="mis-summary-row">
                  <span className="mis-summary-row__label">Interview Type</span>
                  <span className="mis-summary-row__value">{type}</span>
                </div>
                <Separator />
                <div className="mis-summary-row">
                  <span className="mis-summary-row__label">Technology</span>
                  <span className="mis-summary-row__value">{tech}</span>
                </div>
                <Separator />
                <div className="mis-summary-row">
                  <span className="mis-summary-row__label">Difficulty</span>
                  <span className="mis-summary-row__value">{diff}</span>
                </div>
                <Separator />
                <div className="mis-summary-row">
                  <span className="mis-summary-row__label">Duration</span>
                  <span className="mis-summary-row__value">{dur} mins</span>
                </div>
                <Separator />
                <div className="mis-summary-row">
                  <span className="mis-summary-row__label">Role</span>
                  <span className="mis-summary-row__value">{roleName}</span>
                </div>
                <Separator />
                <div className="mis-summary-row">
                  <span className="mis-summary-row__label">Date</span>
                  <span className="mis-summary-row__value">
                    {createdAt ? new Date(createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                  </span>
                </div>
              </div>
            </AppCardContent>
          </AppCard>

          {/* Actions */}
          <div className="mis-result-actions">
            <AppButton className="mis-result-actions__btn" onClick={() => navigate("/s/mock-interview/setup")}>
              <Play size={14} /> Start New Interview
            </AppButton>
            <AppButton variant="outline" className="mis-result-actions__btn" onClick={() => navigate("/s/mock-interview/history")}>
              Back to All Interviews
            </AppButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockInterviewSummary;
